import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { Check, HelpCircle, Loader2 } from 'lucide-react';
import {
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@scio3/ui';
import { ALL_PLAN_FEATURES_QUERY } from '@scio3/graphql-client';
import { getCurrencySymbol, getPlanDisplayName } from '../utils/plan-helpers';

/** Feature from backend PlanFeatures.features array */
interface PlanFeatureItem {
  name?: string;
  description?: string;
  enable?: boolean;
  checked?: boolean;
  isHide?: boolean;
}

/** Plan pricing entry from backend PlanFeatures.plans JSONObject */
interface PlanPriceEntry {
  price?: number;
  name?: string;
  alias?: string;
  text?: string;
  discount_text?: string;
  discount?: number;
  stripe_plan_id?: string;
}

/** Single plan feature document from backend */
interface PlanFeatureNode {
  _id: string;
  alias?: string;
  name?: string;
  description?: string;
  order?: number;
  plans?: Record<string, Record<string, PlanPriceEntry>>;
  features?: PlanFeatureItem[];
  isActive?: boolean;
  learnMoreURL?: string;
}

interface AllPlanFeaturesResponse {
  planFeatures: {
    page: {
      edges: Array<{ node: PlanFeatureNode }>;
    };
  };
}

type PlanComparisonTableProps = {
  value: string;
  onChange: (planFeature: string) => void;
  /** Called when "Select" button is clicked — selects plan AND advances to next step */
  onSelectAndNext?: (planFeature: string) => void;
  /** Called when user selects the Free plan — opens downgrade flow with validation */
  onSelectFree?: () => void;
  /** Currently not used — prices come from backend */
  planPrices?: Record<string, number>;
  currency?: string;
  /** Called when user changes currency in the selector */
  onCurrencyChange?: (currency: string) => void;
  /** Disable currency selector (paid users with existing subscription can't change) */
  currencyDisabled?: boolean;
  currentPlan?: string;
  /** Recommended plan alias (default: proPlus) */
  recommendedPlan?: string;
  /** Minimum plan required — hides plans below this level (by backend order field) */
  minPlanRequired?: string;
};

/**
 * Plan comparison cards matching scio1's step 2 layout.
 * Fetches all plan features from backend and renders as a card grid.
 */
export function PlanComparisonTable({
  value,
  onChange,
  onSelectAndNext,
  onSelectFree,
  currency = 'usd',
  onCurrencyChange,
  currencyDisabled,
  currentPlan,
  recommendedPlan = 'proPlus',
  minPlanRequired,
}: PlanComparisonTableProps) {
  const { t } = useTranslation();

  const { data, loading } = useQuery<AllPlanFeaturesResponse>(ALL_PLAN_FEATURES_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const { plans, freePlan } = useMemo(() => {
    const edges = data?.planFeatures?.page?.edges ?? [];
    const allPlans = edges.map((e) => e.node);
    const free = allPlans.find((n) => n.alias === 'free') ?? null;
    // When minPlanRequired is set, filter out plans below that level (by order field)
    const minOrder = minPlanRequired
      ? (allPlans.find((n) => n.alias === minPlanRequired)?.order ?? 0)
      : 0;
    const paid = allPlans
      .filter((n) => n.isActive !== false && n.alias !== 'free' && (n.order ?? 0) >= minOrder)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return { plans: paid, freePlan: free };
  }, [data, minPlanRequired]);

  // Get monthly price for a plan in the current currency
  const getMonthlyPrice = (plan: PlanFeatureNode): PlanPriceEntry | undefined => {
    const currencyPlans = plan.plans?.[currency];
    if (!currencyPlans) return undefined;
    // Find monthly plan entry
    const monthlyKey = Object.keys(currencyPlans).find((k) => k.includes('monthly'));
    return monthlyKey ? currencyPlans[monthlyKey] : undefined;
  };

  // Visible features for a plan — matching scio1: show all non-hidden features
  // Uses feature.description for display text (not feature.name)
  const getFeatures = (plan: PlanFeatureNode): PlanFeatureItem[] => {
    return (plan.features ?? []).filter((f) => !f.isHide && f.description?.trim());
  };

  // Free plan limitation features (unchecked = limitations like "3 screens limit")
  const freePlanLimitations = useMemo(() => {
    return (freePlan?.features ?? [])
      .filter((f) => !f.isHide && !f.checked && f.description?.trim())
      .slice(0, 4)
      .map((f) => f.description ?? '');
  }, [freePlan]);

  // Free plan included features (checked = what's included, for tooltip)
  const freePlanIncluded = useMemo(() => {
    return (freePlan?.features ?? [])
      .filter((f) => !f.isHide && f.checked && f.description?.trim())
      .map((f) => f.description ?? '');
  }, [freePlan]);

  const currencySymbol = getCurrencySymbol(currency);

  if (loading && plans.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Title + currency selector */}
      <div className="flex items-center justify-end">
        {!minPlanRequired && (
          <h1 className="mr-auto text-xl font-semibold">{t('Select Your Plan')}</h1>
        )}
        <Select value={currency} onValueChange={onCurrencyChange} disabled={currencyDisabled}>
          <SelectTrigger
            className={cn(
              'w-[120px] text-sm',
              currencyDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            )}
            aria-label={t('Currency')}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="usd">USD $</SelectItem>
            <SelectItem value="eur">EUR &euro;</SelectItem>
            <SelectItem value="gbp">GBP &pound;</SelectItem>
            <SelectItem value="cad">CAD $</SelectItem>
            <SelectItem value="aud">AUD $</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Plan cards grid */}
      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: `repeat(${Math.min(plans.length, 4)}, minmax(0, 1fr))` }}
      >
        {plans.map((plan) => {
          const isSelected = value === plan.alias;
          const isRecommended = plan.alias === recommendedPlan;
          const isCurrent = plan.alias === currentPlan;
          const monthlyPrice = getMonthlyPrice(plan);
          const features = getFeatures(plan);
          const priceDollars = Math.floor(monthlyPrice?.price ?? 0);
          const priceCents = ((monthlyPrice?.price ?? 0) % 1).toFixed(2).slice(2);

          return (
            <div
              key={plan._id}
              className={cn(
                'flex flex-col rounded-lg border overflow-hidden cursor-pointer transition-colors',
                isSelected
                  ? 'border-primary shadow-sm scale-[1.02] z-10'
                  : 'border-border hover:border-primary/50',
              )}
              onClick={() => onChange(plan.alias ?? '')}
            >
              {/* Card header — teal for selected plan, white for others.
                  Matching scio1: selected plan gets teal header.
                  "Recommended" badge always shows on proPlus regardless of selection. */}
              <div
                className={cn(
                  'px-4 pt-4 pb-3 text-center rounded-t-lg',
                  isSelected ? 'bg-primary text-white' : 'bg-card',
                )}
              >
                <h3
                  className={cn(
                    'text-lg font-semibold',
                    isSelected ? 'text-white' : 'text-foreground',
                  )}
                >
                  {plan.name || getPlanDisplayName(plan.alias ?? '')}
                </h3>
                {isRecommended && (
                  <Badge
                    className={cn(
                      'mt-1',
                      isSelected
                        ? 'bg-amber-400 text-amber-900 hover:bg-amber-400'
                        : 'bg-amber-400 text-amber-900 hover:bg-amber-400',
                    )}
                  >
                    {t('Recommended')}
                  </Badge>
                )}
              </div>

              {/* Price */}
              <div className="px-4 py-4 text-center">
                <div className="flex items-start justify-center">
                  <span className="text-sm text-primary mt-2">{currencySymbol}</span>
                  <span className="text-4xl font-semibold text-primary">{priceDollars}.</span>
                  <span className="text-lg text-primary mt-1">{priceCents}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('screen')} / {t('month')}
                </p>
                {/* Hardcoded "10% off if paid yearly" matching scio1 line 150 */}
                <Badge className="mt-2 bg-amber-500 text-white hover:bg-amber-500">
                  {t('10% off if paid yearly')}
                </Badge>
              </div>

              {/* Features list — matching scio1: uses description, shows check/info icons */}
              <div className="flex-1 px-4 pb-2">
                <ul className="space-y-1.5">
                  {features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check
                        className={cn(
                          'h-4 w-4 shrink-0 mt-0.5',
                          feature.checked ? 'text-primary' : 'text-muted-foreground/40',
                        )}
                      />
                      <span>{feature.description}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Learn More link — matching scio1 line 161: uses planFeature.learnMoreURL */}
              <div className="px-4 text-center">
                <a
                  href={plan.learnMoreURL ?? 'https://www.optisigns.com/pricing'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary underline cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('Learn More')}
                </a>
              </div>

              {/* Select button — matching scio1 line 164: selects + advances to next step */}
              <div className="px-4 pb-4 pt-2 text-center">
                <Button
                  variant={isCurrent ? 'default' : 'outline'}
                  size="sm"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onSelectAndNext) {
                      onSelectAndNext(plan.alias ?? '');
                    } else {
                      onChange(plan.alias ?? '');
                    }
                  }}
                >
                  {isCurrent ? t('Current Plan') : t('Select')}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Free plan bar — matching scio1: "Free plan (?) — feature1 · feature2 · ... | Select Free" */}
      {!minPlanRequired && (
        <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3 bg-muted/30">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground min-w-0 flex-1">
            <span className="font-medium shrink-0">{t('Free plan')}</span>
            {freePlanIncluded.length > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 shrink-0 text-muted-foreground cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    {freePlanIncluded.map((f, i) => (
                      <div key={i} className="text-xs">
                        ✓ {f}
                      </div>
                    ))}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {freePlanLimitations.length > 0 && (
              <>
                <span className="shrink-0">&mdash;</span>
                <span className="truncate">{freePlanLimitations.join(' · ')}</span>
              </>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="cursor-pointer shrink-0 ml-2"
            onClick={() => {
              if (onSelectFree) {
                onSelectFree();
              } else if (onSelectAndNext) {
                onSelectAndNext('free');
              } else {
                onChange('free');
              }
            }}
          >
            {t('Select Free')}
          </Button>
        </div>
      )}
    </div>
  );
}
